import React from 'react';
import Card from '../Card';
import { Badge, Tabs } from '../ui';
import { HBarChart, TimeChart, formatMs, formatNumber, PALETTE } from '../charts';
import PercentileChart, { latencyHasSamples } from './PercentileChart';

// The Overview's Outbound Internet section: the connections the agents made to the
// internet through proxy-web, the platform's egress proxy. Which site, how many users
// reached it, https or http, what proxy-web did and how long it took; never what was
// sent, and no list of who made each request (Mark, 2026-10-03).
// Data: GET /api/dashboard/outbound, /outbound-timing and /outbound-requests, under the
// page's filters (user, tier and channel; model and source do not apply).
//
// Empty until the environment routes its agents through proxy-web, which the empty
// states say rather than showing a row of blank cards as if something were broken.

const OUTCOMES = [
  { key: 'allowed', label: 'Allowed', color: 'var(--ok-solid)', tone: 'ok' },
  { key: 'blocked', label: 'Blocked', color: 'var(--danger-solid)', tone: 'danger' },
  { key: 'refused', label: 'Refused', color: 'var(--warn-solid)', tone: 'warn' },
  { key: 'failed', label: 'Failed', color: 'var(--ink-4)', tone: 'neutral' },
];
const OUTCOME_BY_KEY = Object.fromEntries(OUTCOMES.map(o => [o.key, o]));

const TIMING_TABS = [
  { value: 'connect', label: 'Connect' },
  { value: 'duration', label: 'Duration' },
];
const RESULT_TABS = [{ value: '', label: 'All' }, ...OUTCOMES
  .filter(o => o.key !== 'allowed').map(o => ({ value: o.key, label: o.label }))];

// What proxy-web writes in `reason` (proxy/web/proxy_web.py), for the person reading the
// table. An unknown code is shown as it is.
const REASONS = {
  blocklist: 'on the blocklist',
  blocklist_address: 'address on the blocklist',
  blocklist_sni: 'on the blocklist (TLS name)',
  private_address: 'private address',
  port: 'port not allowed',
  dns: 'name did not resolve',
  connect_timeout: 'site did not answer',
  connect_error: 'could not connect',
  too_many_connections: 'too many connections',
  connection_lost: 'agent disconnected',
};

const plural = (n, word) => `${formatNumber(n)} ${word}${n === 1 ? '' : 's'}`;

function when(ts) {
  const date = new Date(String(ts || '').replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return ts || '';
  return date.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, timeZone: 'Europe/Rome',
  });
}

function site(row) {
  const standard = (row.kind === 'https' && row.port === 443) || (row.kind === 'http' && row.port === 80);
  return standard || row.port == null ? row.host : `${row.host}:${row.port}`;
}

function reason(row) {
  if (!row.reason) return null;
  // The site is the name TLS was opened for; the tunnel was asked for this address.
  if (row.reason.startsWith('connect=')) return `via ${row.reason.slice(8)}`;
  return REASONS[row.reason] || row.reason;
}

export default function OutboundInternet({
  summary, timing, recent, metric, onMetric, result, onResult, error,
}) {
  const totals = summary?.totals || {};
  const requests = totals.requests || 0;
  const notAllowed = (totals.blocked || 0) + (totals.refused || 0) + (totals.failed || 0);
  const empty = requests > 0 ? null
    : 'No internet requests in this window. Agents are counted here once they go through the platform proxy.';
  const ignored = summary?.ignored || [];
  const sites = (summary?.sites || []).map(s => ({ ...s, name: s.host, count: s.requests }));
  const rows = recent?.rows || [];

  return (
    <>
      <div className="section-head">
        <h2>Outbound Internet</h2>
        <p>the sites agents reached, how often, and how long it took</p>
      </div>

      {error && (
        /* Its own notice, like the LLM requests section's: this data starts empty, so a
           failed endpoint would otherwise look like an environment with no traffic. */
        <div className="notice notice-danger" role="status">
          <div className="notice-body">
            Could not load: {error}. The cards below may be incomplete; the rest of this
            page is unaffected.
          </div>
        </div>
      )}
      {ignored.length > 0 && (
        <p className="text-sm text-muted mb-2">
          The {ignored.join(' and ')} filter does not apply to internet requests, which
          have neither; these cards show every model and source.
        </p>
      )}

      <div className="dashboard-grid">
        <Card title="Internet requests"
              subtitle={requests > 0
                ? `${plural(requests, 'request')} · ${plural(totals.sites || 0, 'site')} · ${plural(totals.users || 0, 'user')}`
                : 'connections agents made, by what the proxy did'}
              help="Every connection an agent opened to the internet, counted when it ended. Allowed went through; Blocked was on the blocklist (Config page); Refused broke a platform rule (a private address or a port other than 80 and 443); Failed could not reach the site. Days older than the Operational logs window come from the daily totals, except when filtering by user, tier or channel."
              span={6}
              empty={empty}>
          <TimeChart data={summary?.data || []} labelKey="date"
                     lines={OUTCOMES.map(({ key, label }) => ({ key, label }))}
                     colors={OUTCOMES.map(o => o.color)}
                     height={240}
                     legend
                     scaleFloor={0} />
          {notAllowed > 0 && (
            <p className="text-sm text-muted mt-2 mb-0">
              Not allowed: {OUTCOMES.filter(o => o.key !== 'allowed' && totals[o.key])
                .map(o => `${formatNumber(totals[o.key])} ${o.label.toLowerCase()}`).join(' · ')}
            </p>
          )}
          {totals.unattributed > 0 && (
            <p className="text-sm text-muted mt-1 mb-0">
              {plural(totals.unattributed, 'request')} came from an address no agent claimed
              and {totals.unattributed === 1 ? 'is' : 'are'} not counted against a user.
            </p>
          )}
        </Card>

        <Card title="Time per request"
              subtitle={metric === 'duration'
                ? 'the whole exchange, of the requests allowed'
                : 'time to connect to the site, of the requests allowed'}
              help={metric === 'duration'
                ? 'From the agent opening the connection to it closing. For https that is the life of the encrypted tunnel, which a client may keep open for several requests, so a long duration is not a slow site.'
                : 'From the proxy starting to connect to the site to the connection being open: the site and the network, not the agent. Refused and failed requests are left out, since they made no exchange.'}
              span={6}
              empty={latencyHasSamples(timing?.data) ? null : 'No allowed requests in this window'}
              actions={
                <Tabs size="sm" label="Time per request metric" value={metric} onChange={onMetric}
                      tabs={TIMING_TABS} />
              }>
          <PercentileChart data={timing?.data || []} />
        </Card>

        <Card title="Sites"
              subtitle="the 10 busiest, by requests"
              help="Requests per site, with how many users reached it. A site whose bar is amber had requests blocked or refused. The users and times cover the detail rows (Operational logs window); older days add only their counts."
              span={6}
              empty={empty}>
          <HBarChart
            data={sites}
            nameWidth={170}
            valueWidth={150}
            formatVal={(v, row) => `${formatNumber(v)} · ${plural(row.users, 'user')}${
              row.median_ms != null ? ` · ${formatMs(row.median_ms)}` : ''}`}
            colorFn={(row) => (row.refused > 0 ? 'var(--warn-solid)' : PALETTE[0])}
          />
          {(totals.sites || 0) > sites.length && (
            <p className="text-sm text-muted mt-2 mb-0">
              The {sites.length} busiest of {formatNumber(totals.sites)} sites.
            </p>
          )}
        </Card>

        <Card title="Recent requests"
              subtitle="newest first"
              help="The latest connections in the window: when, which site, https or http, what the proxy did and how long it took. Only where a connection went is recorded, never what was sent or received."
              span={6}
              empty={rows.length ? null : (result
                ? `No ${OUTCOME_BY_KEY[result]?.label.toLowerCase() || result} requests in this window`
                : empty || 'No requests in this window')}
              actions={
                <Tabs size="sm" label="Requests shown" value={result} onChange={onResult}
                      tabs={RESULT_TABS} />
              }>
          {/* Scrolls inside the card, about the height of the chart beside it. */}
          <div className="table-wrap table-scroll" style={{ '--table-max-h': '300px' }}>
            <table className="table table-compact">
              <caption className="sr-only">
                Recent internet requests by the agents, newest first
              </caption>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Site</th>
                  <th scope="col">Result</th>
                  <th scope="col" className="text-right">Time</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const outcome = OUTCOME_BY_KEY[row.outcome];
                  const why = reason(row);
                  return (
                    <tr key={`${row.timestamp}:${row.host}:${i}`}>
                      <td className="nowrap">{when(row.timestamp)}</td>
                      <td>
                        <code>{site(row)}</code>
                        {row.kind === 'http' && <span className="text-muted"> (http)</span>}
                      </td>
                      <td>
                        <Badge tone={outcome?.tone || 'neutral'}>{outcome?.label || row.outcome}</Badge>
                        {why && <span className="text-sm text-muted"> {why}</span>}
                      </td>
                      <td className="text-right nowrap">
                        {row.duration_ms != null ? formatMs(row.duration_ms) : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}
